import { createContext, FunctionComponent, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { createBrowserHistory } from 'history';
import { OnlybotsScene } from '@/lib/types';
import { deviceProperties } from '@/lib/deviceProperties';
import { useEffectContext } from '@/hooks/useEffectContext';
import { MOSAIC_TRANSITION_DURATION } from '@/lib/globals';

type PathData = {
    view:
        | { name: 'THREE'; scene: OnlybotsScene }
        | { name: 'TALK' }
        | { name: 'POINTS' }
        | { name: 'DISCORD' }
        | { name: 'STL' };
    tokenId: number;
};

type RouterContextValue = PathData & {
    setScene: (scene: OnlybotsScene) => void;
    navigate: (path: string) => void;
};

const context = createContext<RouterContextValue | null>(null);

export const useRouterContext = (): RouterContextValue => {
    const value = useContext(context);
    if (!value) {
        throw new Error('useRouterContext must be used within a RouterContextProvider!');
    }
    return value;
};

// Make sure to update build.js with any new routes
const parsePath = (): PathData => {
    const path = window.location.pathname;
    const { searchParams } = new URL(window.location.href);
    const isReveal = searchParams.has('hi');

    if (path === '/connect') {
        history.replace('/magicswan');
        return {
            view: {
                name: 'THREE',
                scene: 'CONNECT',
            },
            tokenId: 0,
        };
    }

    if (path === '/download') {
        const tokenId = parseInt(searchParams.get('id') ?? '0', 10);
        if (isNaN(tokenId) || tokenId <= 0) {
            return {
                view: {
                    name: 'THREE',
                    scene: 'NOTFOUND',
                },
                tokenId: 0,
            };
        }

        return {
            view: {
                name: 'THREE',
                scene: 'DOWNLOAD',
            },
            tokenId: isNaN(tokenId) ? 0 : tokenId,
        };
    }

    if (path === '/talk') {
        return {
            view: {
                name: 'TALK',
            },
            tokenId: 0,
        };
    }

    if (path === '/points') {
        return {
            view: {
                name: 'POINTS',
            },
            tokenId: 0,
        };
    }

    if (path === '/magicswan') {
        return {
            view: {
                name: 'THREE',
                scene: 'LOADING',
            },
            tokenId: 0,
        };
    }

    if (path === '/discord/connect') {
        return {
            view: {
                name: 'DISCORD',
            },
            tokenId: 0,
        };
    }

    if (path === '/stl') {
        return {
            view: {
                name: 'STL',
            },
            tokenId: 0,
        };
    }

    if (path === '/') {
        console.error('REDIRECTING TO /magicswan, REPLACE THIS LINK!');
        history.replace('/magicswan');
        return {
            view: {
                name: 'THREE',
                scene: 'LOADING',
            },
            tokenId: 0,
        };
    }

    if (path.match(/^\/\d+$/)) {
        const tokenIdString = path.slice(1);
        const tokenId = parseInt(tokenIdString, 10);
        if (!isNaN(tokenId) && tokenId > 0) {
            // convert param to int, then back to string to strip any leading zeros
            const check = tokenId.toString(10);
            if (check !== tokenIdString) {
                history.replace(`/${check}`);
            }

            return {
                view: {
                    name: 'THREE',
                    scene: isReveal ? 'REVEAL' : 'EDITIONLOADING',
                },
                tokenId,
            };
        }
    }

    return {
        view: {
            name: 'THREE',
            scene: 'NOTFOUND',
        },
        tokenId: 0,
    };
};

const history = createBrowserHistory();
const { isMobileSafari } = deviceProperties();

export const RouterContextProvider: FunctionComponent = ({ children }) => {
    const { Provider } = context;

    const [{ view, tokenId }, setData] = useState<PathData>(() => parsePath());
    const { setPixelate } = useEffectContext();

    const setScene = useCallback((scene: OnlybotsScene) => {
        setData((current) => ({ ...current, view: { name: 'THREE', scene } }));
    }, []);

    useEffect(() => {
        const historyListener = history.listen(() => {
            setData(parsePath());
        });
        return historyListener; // calling this unsubscribes to history
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const navigate = useCallback(
        (path: string) => {
            if (isMobileSafari) {
                // TODO: only do this when app clip state needs changing (i.e. to and from edition page)
                const url = new URL(window.location.href);
                url.searchParams.delete('hi'); // remove 'hi' query param
                url.pathname = path;
                window.location.href = url.href;
                return;
            }

            setPixelate(true);

            setTimeout(() => {
                history.push(path);
                setPixelate(false);
                setData(parsePath());
            }, MOSAIC_TRANSITION_DURATION * 1000);
        },
        [setPixelate]
    );

    const value = useMemo<RouterContextValue>(
        () => ({
            view,
            tokenId,
            setScene,
            navigate,
        }),
        [view, tokenId, setScene, navigate]
    );
    return <Provider value={value}>{children}</Provider>;
};
